import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/errors/use-toast';
import { useContextAwareAIUsage } from '@/hooks/ai-usage/useContextAwareAIUsage';
import { useAIOperationWrapper } from '@/hooks/ai-usage/useAIOperationWrapper';
import { validateGenerationOptions } from '@/integrations/runware/utils/validation';
import type { ImageGenerationOptions, GeneratedImage } from '@/integrations/runware/types';

// Re-export types from the central location for backward compatibility
export type { ImageGenerationOptions, GeneratedImage, RunwareImageGenerationOptions } from '@/integrations/runware/types';

export const useImageGeneration = () => {
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([]);
  const { toast } = useToast();
  const { createImageGenerationContext } = useContextAwareAIUsage();
  const { executeAIOperation, isLoading, error } = useAIOperationWrapper();
  
  const generateImage = async (options: ImageGenerationOptions): Promise<GeneratedImage[]> => {
    return executeAIOperation(
      async () => {
        console.log('[useImageGeneration] Starting image generation with options:', options);
        
        // Use the same validation as the integration layer
        const validationError = validateGenerationOptions(options);
        if (validationError) {
          console.error(`[useImageGeneration] ${validationError}`);
          toast({
            title: 'Image generation failed',
            description: validationError,
            variant: 'error',
          });
          throw new Error(validationError);
        }
        
        console.log('[useImageGeneration] Calling generate-image function with options:', options);
        
        // Get context-aware app and function names
        const usageContext = createImageGenerationContext();
        console.log('[useImageGeneration] Detected usage context:', usageContext);
        
        // Send request to supabase edge function
        const { data, error: functionError } = await supabase.functions.invoke('generate-image', {
          body: {
            ...options,
            appName: usageContext.appName,
            functionName: usageContext.functionName
          }
        });
        
        if (functionError) {
          console.error('[useImageGeneration] Edge function error:', functionError);
          toast({
            title: 'Image generation failed',
            description: functionError.message || 'Error communicating with server',
            variant: 'error',
          });
          throw new Error(functionError.message);
        }
        
        console.log('[useImageGeneration] Received response from generate-image function:', data);
        
        if (!data || data.error) {
          console.error('[useImageGeneration] API error:', data?.error || 'No data received');
          toast({
            title: 'Image generation failed',
            description: data?.error || 'Unknown error occurred',
            variant: 'error',
          });
          throw new Error(data?.error || 'Unknown error occurred');
        }
        
        // Extract image data from response
        if (!data.data || !Array.isArray(data.data)) {
          console.error('[useImageGeneration] Invalid response format:', data);
          toast({
            title: 'Image generation failed',
            description: 'Invalid response from server',
            variant: 'error',
          });
          throw new Error('Invalid response format');
        }
        
        // 🆕 Extract the enhanced data from Edge Function response
        const totalCost = data.totalCost || 0;
        const fullPrompt = data.fullPrompt || '';
        
        console.log('[useImageGeneration] Enhanced response data:', {
          totalCost,
          fullPrompt: fullPrompt.substring(0, 100) + '...',
          metadata: data.metadata
        });
        
        // Extract image data from response
        const imageResults = data.data
          .filter((item: any) => item.taskType === 'imageInference')
          .map((item: any) => ({
            imageURL: item.imageURL,
            positivePrompt: item.positivePrompt || fullPrompt || options.positivePrompt || 'Generated image',
            seed: item.seed,
            NSFWContent: item.NSFWContent || false,
            imageUUID: item.imageUUID,
            cost: item.cost || totalCost, // Use individual cost or total cost
            // 🆕 Add enhanced data for database saving
            totalCost: totalCost,
            fullPrompt: fullPrompt
          }));
        
        console.log('[useImageGeneration] Processed image results:', imageResults);
        
        if (imageResults.length === 0) {
          toast({
            title: 'No images generated',
            description: 'The image generation service did not return any images',
            variant: 'warning',
          });
          throw new Error('No images generated');
        }
        
        // Update local state
        setGeneratedImages(imageResults);
        
        toast({
          title: 'Images generated successfully',
          description: `Generated ${imageResults.length} image${imageResults.length > 1 ? 's' : ''}`,
          variant: 'success',
        });
        
        return imageResults;
      },
      {
        immediate: true, // Image generation needs immediate feedback
        errorPrefix: 'Image generation failed',
        debug: true // Enable debug logging for image generation
      }
    );
  };

  const clearImages = () => {
    setGeneratedImages([]);
  };

  return {
    generateImage,
    clearImages,
    generatedImages,
    isLoading,
    error,
  };
};
